import { useMemo } from "react";

import { latestInvitationForMembership } from "@/models/repositories/Driver";
import { useCollectionRow, useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { DriverRow, TagDefinition } from "@/types/driver";
import { driverDialNumber, phoneTagValue } from "./driverPhone";

/**
 * The number the Dispatch Call button dials for a driver, in E.164, or `null` when no source holds a dialable one.
 *
 * Reads the account and the latest invitation from the store reactively, so a number that syncs in while the popover
 * is open updates the header; the phone tag's value already arrives on the driver row.
 *
 * @param driver - The popover's driver; `null` while the driver is not in the current roster.
 * @param tags - The workspace's tag definitions, searched for the phone tag by slug.
 * @param phoneTagSlug - The workspace's `web.dispatch.phoneTagSlug`; `null` reads no tag.
 */
export function useDriverDialNumber(
  driver: DriverRow | null,
  tags: readonly TagDefinition[],
  phoneTagSlug: string | null,
): string | null {
  const accountPhone = useCollectionRow("account", driver?.accountId)?.phoneNumber ?? null;
  const membershipId = driver?.workspaceMembershipId;
  const invitationPhone = useCollectionsCompute(
    ["directWsInvitation"],
    () => (membershipId ? (latestInvitationForMembership(membershipId)?.phoneNumber ?? null) : null),
    [membershipId],
    "dispatchInvitationPhone",
  );
  const tagPhone = driver ? phoneTagValue(driver, tags, phoneTagSlug) : null;
  return useMemo(
    () => driverDialNumber({ accountPhone, invitationPhone, tagPhone }),
    [accountPhone, invitationPhone, tagPhone],
  );
}
